import { zodResolver } from "@hookform/resolvers/zod";
import {
    Button,
    Icon,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Controller, DefaultValues, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import {
    BusinessLine,
    RegistrationRequestIntroductionMethod,
} from "../../../../api/psc";
import AppAutoComplete, {
    createOptionsFromArray,
    createOptionsFromKeyValue,
} from "../../../../components/shared/AppAutoComplete";
import { provinces } from "../../../../constants/Provinces";

// Types
export type SignUpForm = {
    fullName: string;
    businessLineIds: number[];
    stateId?: number;
    companyName: string;
    introductionMethod: RegistrationRequestIntroductionMethod;
};

interface SignUpFormStepProps {
    sendSignUpForm: (signUpForm: SignUpForm) => void;
    businessLines: BusinessLine[];
    stepBack: () => void;
    isSubmitting?: boolean;
}

// Constants
const INTRODUCTION_METHODS = {
    [RegistrationRequestIntroductionMethod.SALE_OPERATOR]: "کارشناسان فروش",
    [RegistrationRequestIntroductionMethod.INTERNET]: "جستجو در اینترنت",
    [RegistrationRequestIntroductionMethod.WORD_OF_MOTH]: "معرفی آشنایان",
    [RegistrationRequestIntroductionMethod.SOCIAL_MEDIA]: "شبکه اجتماعی",
    [RegistrationRequestIntroductionMethod.OTHERS]: "سایر",
} as const;

// Validation Schema
const validationSchema = z.object({
    fullName: z.string().min(1, "نام و نام خانوادگی الزامی است"),
    businessLineIds: z
        .array(z.any())
        .min(1, "حداقل یک زمینه فعالیت انتخاب کنید"),
    stateId: z.number().optional(),
    companyName: z.string().min(1, "نام شرکت الزامی است"),
    // @ts-ignore
    introductionMethod: z.enum(RegistrationRequestIntroductionMethod, {
        message: "نحوه معرفی الزامی است",
    }),
});

// Default Values
const DEFAULT_VALUES: DefaultValues<SignUpForm> = {
    fullName: "",
    businessLineIds: [],
    stateId: null,
    companyName: "",
    introductionMethod: "" as RegistrationRequestIntroductionMethod,
};

// Using simple checkboxes without external icons

export default function SignUpFormStep(props: SignUpFormStepProps) {
    const {
        register,
        handleSubmit,
        setValue,
        control,
        watch,
        formState: { errors, isSubmitting },
    } = useForm<SignUpForm>({
        defaultValues: DEFAULT_VALUES,
        resolver: zodResolver(validationSchema),
    });

    const searchParams = useSearchParams();
    const social = searchParams[0].get("social");
    if (social) {
        setValue(
            "introductionMethod",
            RegistrationRequestIntroductionMethod.SOCIAL_MEDIA
        );
    }

    const onSubmit = (data: SignUpForm) => {
        props.sendSignUpForm(data);
    };

    const handleFormSubmit = () => {
        handleSubmit(onSubmit)();
    };

    return (
        <Stack spacing={3}>
            {/* Header Section */}
            <Stack spacing={1}>
                <Typography
                    variant="h6"
                    component="h2"
                    textAlign="center"
                    sx={{ whiteSpace: "nowrap" }}
                >
                    ما می‌توانیم با شناخت بیشتر شما خدمات بهتری ارائه دهیم
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                >
                    لطفاً اطلاعات خود را وارد کنید
                </Typography>
            </Stack>

            {/* Form Fields */}
            <Stack spacing={2}>
                {/* Full Name Field */}
                <TextField
                    {...register("fullName")}
                    label="نام و نام خانوادگی"
                    error={!!errors.fullName}
                    helperText={errors.fullName?.message}
                    fullWidth
                    required
                />

                {/* Company Name Field */}
                <TextField
                    {...register("companyName")}
                    label="نام شرکت"
                    error={!!errors.companyName}
                    helperText={errors.companyName?.message}
                    fullWidth
                    required
                />

                {/* Business Field Autocomplete */}
                <Controller
                    control={control}
                    name="businessLineIds"
                    render={({ field }) => {
                        // Get parent and child business lines
                        const parentBusinessLines = props.businessLines.filter(
                            (line) => !line.parentId
                        );
                        const childBusinessLines = props.businessLines.filter(
                            (line) => line.parentId
                        );

                        // Create options with grouped structure: Parent followed by its children
                        const groupedOptions = parentBusinessLines.flatMap(
                            (parent) => {
                                // Parent option
                                const parentOption = createOptionsFromArray(
                                    [parent],
                                    (line) => line.title || "",
                                    (line) => line.id || 0
                                ).map((option) => ({
                                    ...option,
                                    isGroupHeader: true,
                                }))[0];

                                // Child options for this parent
                                const childOptionsForParent =
                                    createOptionsFromArray(
                                        childBusinessLines.filter(
                                            (child) =>
                                                child.parentId === parent.id
                                        ),
                                        (line) => line.title || "",
                                        (line) => line.id || 0
                                    );

                                // Return parent followed by its children
                                return [parentOption, ...childOptionsForParent];
                            }
                        );

                        // Also add any orphaned children (children without a valid parent)
                        const orphanedChildren = createOptionsFromArray(
                            childBusinessLines.filter(
                                (child) =>
                                    !parentBusinessLines.find(
                                        (p) => p.id === child.parentId
                                    )
                            ),
                            (line) => line.title || "",
                            (line) => line.id || 0
                        );

                        const allOptions = [
                            ...groupedOptions,
                            ...orphanedChildren,
                        ];

                        return (
                            <AppAutoComplete
                                label="زمینه فعالیت"
                                placeholder="انتخاب زمینه فعالیت..."
                                options={allOptions}
                                value={(field.value as any) || []}
                                onChange={(newValue) =>
                                    field.onChange(newValue || [])
                                }
                                error={!!errors.businessLineIds}
                                helperText={errors.businessLineIds?.message}
                                required
                                multiple={true}
                                disableCloseOnSelect={true}
                                showCheckboxes={true}
                                showChips={true}
                                chipVariant="outlined"
                                enableSelectableGroupHeaders={true}
                                noOptionsText="نتیجه‌ای یافت نشد"
                                getOptionValue={(option) => option.id}
                            />
                        );
                    }}
                />

                {/* Introduction Method Autocomplete */}
                {!social && (
                    <Controller
                        control={control}
                        name="introductionMethod"
                        render={({ field }) => (
                            <AppAutoComplete
                                label="نحوه معرفی"
                                placeholder="انتخاب نحوه معرفی..."
                                options={createOptionsFromKeyValue(
                                    INTRODUCTION_METHODS
                                ).map((option) => ({
                                    ...option,
                                    value: option.id as RegistrationRequestIntroductionMethod,
                                }))}
                                value={(field.value as any) || null}
                                onChange={(newValue) =>
                                    field.onChange(newValue)
                                }
                                error={!!errors.introductionMethod}
                                helperText={errors.introductionMethod?.message}
                                required
                                getOptionValue={(option) => option.id}
                            />
                        )}
                    />
                )}

                {/* State Autocomplete */}
                <Controller
                    control={control}
                    name="stateId"
                    render={({ field }) => {
                        const provinceOptions = createOptionsFromArray(
                            provinces,
                            (province) => province.provinceName,
                            (province) => province.provinceId
                        );

                        return (
                            <AppAutoComplete
                                label="استان"
                                placeholder="انتخاب استان..."
                                options={provinceOptions}
                                value={(field.value as any) || null}
                                onChange={(newValue) => {
                                    setValue("stateId", newValue as any);
                                    field.onChange(newValue || null);
                                }}
                                error={!!errors.stateId}
                                helperText={errors.stateId?.message}
                                required
                                getOptionValue={(option) =>
                                    option.value.provinceId
                                }
                            />
                        );
                    }}
                />
            </Stack>

            {/* Submit Button */}
            <Stack direction="row" gap={2}>
                <Button
                    variant="outlined"
                    onClick={props.stepBack}
                    sx={{ flex: 1 }}
                >
                    بازگشت
                </Button>

                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleFormSubmit}
                    disabled={props.isSubmitting}
                    endIcon={<Icon>arrow_back</Icon>}
                    sx={{ py: 1.5 }}
                >
                    {props.isSubmitting ? "در حال ثبت..." : "ثبت‌نام"}
                </Button>
            </Stack>
        </Stack>
    );
}
